import * as React from 'react';
import styled, { css } from 'styled-components';
import { IDateInfo, SelectionMode, ICalendarOnRenderItem } from '../calendar-types';
import { Typography } from '../../typography';
import { useChooseRef, useKeyStrokeListener } from '../../../utils';

export interface ICalandarDateProps extends ICalendarOnRenderItem {
    className?: string;
    disabled: boolean;
    info: IDateInfo;
    isSelected?: boolean;
    isInCurentMonth?: boolean;
    selectionMode?: SelectionMode;
    isToday?: boolean;
    dateInRange?: boolean;
    onSelect(item: IDateInfo): void;
    onUnselect(item: IDateInfo): void;
    onDateCellMouseOver(item: IDateInfo): void;
}

function CalendarDateBase(props: ICalandarDateProps) {
    const {
        info, onSelect, onUnselect, className = '', disabled, isSelected, selectionMode,
        onDateCellMouseOver, onRenderItem, isToday
    } = props;
    const { date } = info;
    const elementRef = useChooseRef(React.createRef()) as React.RefObject<HTMLSpanElement>;

    const { subscribe, unsubscribe } = useKeyStrokeListener(elementRef);

    const _onClick = React.useCallback(() => {
        if (disabled) {
            return;
        }
        (isSelected && selectionMode === 'multiple') ? onUnselect(info) : onSelect(info);
    }, [disabled, info, onSelect, onUnselect, selectionMode, isSelected]);

    const _onDateCellMouseOver = React.useCallback(() => {
        if (disabled) {
            return;
        }
        onDateCellMouseOver(info);
    }, [disabled, info, onDateCellMouseOver]);

    React.useEffect(() => {
        subscribe(_onClick, 'Enter');
        return unsubscribe;
    }, [subscribe, unsubscribe, _onClick]);

    const spanAttrs: React.HTMLProps<HTMLSpanElement> = React.useMemo(() => ({
        className,
        tabIndex: 0,
        role: 'button',
        ref: elementRef,
        title: date.toString(),
        onClick: _onClick,
        onMouseOver: selectionMode === 'range' ? _onDateCellMouseOver : undefined
    }), [className, elementRef, date, _onClick, selectionMode, _onDateCellMouseOver]);

    if (onRenderItem !== undefined) {
        return onRenderItem({ dateInfo: info, onClick: _onClick, isSelected, isToday, isDisabled: disabled });
    }

    return React.createElement(Typography.Medium, spanAttrs, date);
}

const selectedStyle = css`
    background-color: ${({ theme: { calendar } }) => calendar.colors.selectedDate.backgroundColor};
    color: ${({ theme: { calendar } }) => calendar.colors.selectedDate.foregroundColor};
    &:hover{
        background-color: ${({ theme: { calendar } }) => calendar.colors.selectedDateHover.backgroundColor};
        color: ${({ theme: { calendar } }) => calendar.colors.selectedDateHover.foregroundColor};
    }
`;

const disabledStyle = css`
    cursor: not-allowed;
    background-color: ${({ theme: { calendar } }) => calendar.colors.disabled.backgroundColor};
    color: ${({ theme: { calendar } }) => calendar.colors.disabled.foregroundColor};
    &:hover{
        background-color: ${({ theme: { calendar } }) => calendar.colors.disabledHover.backgroundColor};
        color: ${({ theme: { calendar } }) => calendar.colors.disabledHover.foregroundColor};
    }
`;

const todayDateStyle = css`
    color: ${({ theme: { calendar } }) => calendar.colors.currentDate.foregroundColor};
    position: relative;
    &:after {
        content: "";
        position: absolute;
        width: 100%;
        height: 2px;
        background-color: ${({ theme: { calendar } }) => calendar.colors.currentDate.backgroundColor};
        bottom: 1px;
        left: 0
    };
    &:hover:after {
        display: none
    }
`;

const dateRangeStyle = css`
    color: ${({ theme: { calendar } }) => calendar.colors.dateRange.foregroundColor};
`;

const dateFromOtherMonthStyle = css`
    color: ${({ theme: { calendar } }) => calendar.colors.otherMonthDate.foregroundColor};
    background-color: ${({ theme: { calendar } }) => calendar.colors.otherMonthDate.backgroundColor};
    &:hover{
        color: ${({ theme: { calendar } }) => calendar.colors.otherMonthDateHover.foregroundColor};
        background-color: ${({ theme: { calendar } }) => calendar.colors.otherMonthDateHover.backgroundColor};
    }
`;

export const CalendarDate = styled(CalendarDateBase as React.ComponentType<ICalandarDateProps>).attrs({
    className: String('ap-calendar-date')
})`
    display: inline-block;
    height: 30px;
    width: 30px;
    line-height: 30px;
    border-radius: 100%;
    text-align: center;
    cursor: pointer;
    box-sizing: border-box;
    user-select: none;
    color: ${({ theme: { calendar } }) => calendar.colors.defaultDate.foregroundColor};
    
    &:hover {
        background-color: ${({ theme: { calendar } }) => calendar.colors.hover.backgroundColor};
        color: ${({ theme: { calendar } }) => calendar.colors.hover.foregroundColor};
    }

    ${({ isSelected, isInCurentMonth }) => (isSelected && isInCurentMonth) && selectedStyle}
    ${({ isInCurentMonth }) => !isInCurentMonth && dateFromOtherMonthStyle}
    ${({ isSelected, isToday }) => (!isSelected && isToday) && todayDateStyle}
    ${({ disabled }) => disabled && disabledStyle}
    ${({ dateInRange, isSelected }) => (dateInRange && !isSelected) && dateRangeStyle}
`;
